import {
  countryDisplayName,
  enabledDestinations,
  type ShippingAddress,
  shippingAddressFieldIssue,
  shippingAddressSchema,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  ADDRESS_ISSUE_MESSAGES,
  toShippingAddressDraft,
  withoutBlanks,
} from "../lib/shippingAddressDraft";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { FactGroup } from "./Facts";
import { ShippingAddressFields } from "./ShippingAddressFields";

/**
 * The end user's shipping address as staff record it (boxed parity: the Account Manager's
 * Update Address form). The country is one of the environment's enabled shipping destinations,
 * the same list the shopper's own form offers, and a US address picks a state. An order copies
 * its own address at creation; this one is a reference. Editors get an Add/Edit dialog in the
 * heading; viewers read.
 */
export function ShippingAddressSection({
  endUserId,
  address,
}: {
  endUserId: string;
  address: ShippingAddress | null;
}) {
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const storefront = useStorefrontConfigQuery().query.data;
  const destinations = storefront
    ? enabledDestinations(
        storefront.config.shippingDestinations ?? storefront.shipping.platformDefault,
      )
    : [];
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => toShippingAddressDraft(address));
  const save = useMutation(
    trpc.users.setShippingAddress.mutationOptions({
      onSuccess: (result) => {
        toast.success(result.shippingAddress ? "Address saved." : "Address cleared.");
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
      },
    }),
  );
  const parsed = shippingAddressSchema.safeParse(withoutBlanks(draft));
  const issue = parsed.success ? shippingAddressFieldIssue(parsed.data) : null;
  const canSave = parsed.success && issue === null && destinations.length > 0;

  return (
    <FactGroup
      title="Shipping address"
      action={
        can("editor") && (
          <Dialog
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (next) setDraft(toShippingAddressDraft(address));
            }}
          >
            <DialogTrigger render={<Button type="button" variant="ghost" size="sm" />}>
              {address ? "Edit" : "Add"}
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Shipping address</DialogTitle>
                <DialogDescription>
                  Every field except the second line and the phone number is required; the country
                  is one this environment ships to.
                </DialogDescription>
              </DialogHeader>
              <form
                className="grid gap-3 sm:grid-cols-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (parsed.success && canSave) {
                    save.mutate({ endUserId, shippingAddress: parsed.data });
                  }
                }}
              >
                <ShippingAddressFields
                  idPrefix="address"
                  draft={draft}
                  countries={destinations.map((d) => d.countryCode)}
                  onChange={setDraft}
                />
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  {destinations.length === 0
                    ? "Shipping is not configured for this environment (the tenant's Shipping page)."
                    : issue
                      ? ADDRESS_ISSUE_MESSAGES[issue]
                      : null}
                </p>
                <DialogFooter className="sm:col-span-2">
                  {address && (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={save.isPending}
                      onClick={() => save.mutate({ endUserId, shippingAddress: null })}
                    >
                      Clear address
                    </Button>
                  )}
                  <DialogClose render={<Button type="button" variant="outline" />}>
                    Cancel
                  </DialogClose>
                  <Button type="submit" disabled={save.isPending || !canSave}>
                    {save.isPending ? "Saving…" : "Save address"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )
      }
    >
      {address ? (
        <address className="grid gap-0.5 text-sm not-italic">
          <span>
            {address.firstName} {address.lastName}
          </span>
          <span>{address.addressLine1}</span>
          {address.addressLine2 && <span>{address.addressLine2}</span>}
          <span>
            {address.locality}, {address.region} {address.postalCode}
          </span>
          <span>{countryDisplayName(address.country)}</span>
          {address.phoneNumber && (
            <span className="text-muted-foreground">{address.phoneNumber}</span>
          )}
        </address>
      ) : (
        <p className="text-sm text-muted-foreground">None on file.</p>
      )}
    </FactGroup>
  );
}
